import * as React from 'react'
import { useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import { Button, Stack, TablePagination, Typography } from '@mui/material'
import { stableSort, getComparator, Order, HeadCell } from '../../../utils/tableFunctions'
import EnhancedTableHead from '../../../components/table/EnhancedTableHead'
import TableButtonDelete from '../../../components/table/TableButtonDelete'
import TableButtonOpen from '../../../components/table/TableButtonOpen'
import { RouteConstants } from '../../../navigation/navigation-types'
import CustomTableCell from '../../../components/common/CustomTableCell'
import { EmailMessageDto } from '../../../services/api/sharedTypes/Email'
import EditEmailDialog from '../../../components/emails/EditEmailDialog'
import TableButtonEdit from '../../../components/table/TableButtonEdit'

const headCells: readonly HeadCell[] = [
    {
        id: 'eventName',
        numeric: false,
        disablePadding: false,
        label: 'Událost',
    },
    {
        id: 'clientName',
        numeric: false,
        disablePadding: false,
        label: 'Klient',
    },
    {
        id: 'subject',
        numeric: false,
        disablePadding: false,
        label: 'Předmět',
    },
    {
        id: 'createdAt',
        numeric: false,
        disablePadding: false,
        label: 'Vytvořeno',
    },
    {
        id: 'buttons',
        numeric: false,
        disablePadding: false,
        label: '',
    },
]

interface WaitingEmailsTableProps {
    emails: EmailMessageDto[]
    onDelete: (id: string) => void
    onUpdate: (id: string, subject: string, emailBody: string, updateAndSendEmailMessage?: boolean) => void
}

const WaitingEmailsTable: React.FC<WaitingEmailsTableProps> = ({ emails, onDelete, onUpdate }: WaitingEmailsTableProps) => {
    const [order, setOrder] = React.useState<Order>('desc')
    const [orderBy, setOrderBy] = React.useState<string>('createdAt')
    const [page, setPage] = React.useState(0)
    const [rowsPerPage, setRowsPerPage] = React.useState(10)

    const theme = useTheme()

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    return (
        <>
            <Typography variant='body2' style={{ marginBottom: theme.spacing(3) }}>
                {emails.length} záznamů
            </Typography>
            <Box style={{ width: '100%' }}>
                <TableContainer>
                    <Table size='small'>
                        <EnhancedTableHead
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                            rowCount={emails.length}
                        />
                        <TableBody>
                            {stableSort(
                                emails.map((email) => {
                                    return {
                                        ...email,
                                        createdAt: new Date(email.createdAt).getTime(),
                                        lastModifiedAt: new Date(email.lastModifiedAt).toLocaleDateString(),
                                        eventIdName: email.eventIdName?.name ?? '',
                                        eventId: email.eventIdName?.id ?? '',
                                        eventName: email.eventIdName?.name ?? '',
                                        clientIdName: email.clientIdName?.name ?? '',
                                        clientId: email.clientIdName?.id ?? '',
                                        clientName: email.clientIdName?.name ?? '',
                                    }
                                }),
                                getComparator(order, orderBy)
                            )
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((row, index: number) => {
                                    return (
                                        <TableRow hover tabIndex={-1} key={index}>
                                            <CustomTableCell
                                                style={{ cursor: 'pointer' }}
                                                route={RouteConstants.events + '/' + row.eventId}
                                                isText
                                            >
                                                {row.eventIdName}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                style={{ cursor: 'pointer' }}
                                                route={RouteConstants.clients + '/' + row.clientId}
                                                isText
                                            >
                                                {row.clientName}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                style={{ cursor: 'pointer' }}
                                                route={RouteConstants.events + '/' + row.eventId}
                                                isText
                                            >
                                                {row.subject}
                                            </CustomTableCell>
                                            <CustomTableCell
                                                style={{ cursor: 'pointer' }}
                                                route={RouteConstants.events + '/' + row.eventId}
                                                isText
                                            >
                                                {new Date(row.createdAt).toLocaleDateString('cs')}
                                            </CustomTableCell>
                                            <CustomTableCell align='right' style={{ minWidth: 300 }}>
                                                <Stack spacing={2} direction='row' alignItems='center' justifyContent='end'>
                                                    <EditEmailDialog
                                                        onSend={onUpdate}
                                                        type='sendAndEdit'
                                                        email={row}
                                                        button={
                                                            <Button style={{ fontSize: 14 }}>
                                                                Upravit a poslat
                                                            </Button>
                                                        }
                                                    />
                                                    <EditEmailDialog
                                                        type='edit'
                                                        onSend={onUpdate}
                                                        email={row}
                                                        button={<TableButtonEdit />}
                                                    />
                                                    <TableButtonOpen route={RouteConstants.events} id={row.eventId} />
                                                    <TableButtonDelete
                                                        nameOfEntityInDialogMessage={row.eventName}
                                                        onClick={() => onDelete(row.id)}
                                                    />
                                                </Stack>
                                            </CustomTableCell>
                                        </TableRow>
                                    )
                                })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component='div'
                    count={emails.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Box>
        </>
    )
}

export default WaitingEmailsTable
